import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AppProvider, useApp } from '@/context/AppContext';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import AppHeader from '@/components/common/AppHeader';
import ProgressIndicator from '@/components/common/ProgressIndicator';
import PhotoUploadStep from '@/components/steps/PhotoUploadStep';
import StyleSelectionStep from '@/components/steps/StyleSelectionStep';
import ResultStep from '@/components/steps/ResultStep';
import './services/i18n';

function AppContent() {
  const { i18n } = useTranslation();
  const { state, dispatch } = useApp();

  // Initialize language from URL or localStorage
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const langFromUrl = urlParams.get('lang');
    
    if (langFromUrl && ['en', 'zh'].includes(langFromUrl)) {
      i18n.changeLanguage(langFromUrl);
      dispatch({ type: 'SET_LANGUAGE', payload: langFromUrl as 'en' | 'zh' });
    } else {
      // Use current i18n language (from detector)
      dispatch({ type: 'SET_LANGUAGE', payload: i18n.language as 'en' | 'zh' });
    }
  }, [i18n, dispatch]);

  // Handle browser navigation
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const langFromUrl = urlParams.get('lang');
      
      if (langFromUrl && ['en', 'zh'].includes(langFromUrl)) {
        i18n.changeLanguage(langFromUrl);
        dispatch({ type: 'SET_LANGUAGE', payload: langFromUrl as 'en' | 'zh' });
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [i18n, dispatch]);

  const renderCurrentStep = () => {
    switch (state.currentStep) {
      case 1:
        return <PhotoUploadStep />;
      case 2:
        return <StyleSelectionStep />;
      case 3:
        return <ResultStep />;
      default:
        return <PhotoUploadStep />;
    }
  };

  return (
    <div className="app-container">
      <AppHeader />
      <ProgressIndicator />
      <main>
        {renderCurrentStep()}
      </main>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppProvider>
        <AppContent />
      </AppProvider>
    </ErrorBoundary>
  );
}